"use client"

import { useState } from "react"
import { toast } from "sonner"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { Separator } from "@/components/ui/separator"
import { RefreshCw, TrendingUp } from "lucide-react"
import { useCases } from "@/lib/mock-data"
import { PriorityBadge } from "@/components/status-badges"
import type { PriorityBand } from "@/lib/types"

interface ScoreWeights {
  businessImpact: number
  riskComplianceValue: number
  feasibility: number
  urgency: number
  reusability: number
  dataReadiness: number
}

function calcScore(weights: ScoreWeights): number {
  const raw =
    weights.businessImpact * 0.3 +
    weights.riskComplianceValue * 0.2 +
    weights.feasibility * 0.2 +
    weights.urgency * 0.15 +
    weights.reusability * 0.1 +
    weights.dataReadiness * 0.05
  return Math.round((raw / 5) * 100)
}

function calcBand(score: number): PriorityBand {
  if (score >= 80) return "P1"
  if (score >= 60) return "P2"
  if (score >= 40) return "P3"
  return "P4"
}

type UCScores = Record<string, ScoreWeights>

function initScores(): UCScores {
  return Object.fromEntries(
    useCases.map((uc) => [
      uc.id,
      {
        businessImpact: uc.businessImpact ?? 3,
        riskComplianceValue: uc.riskComplianceValue ?? 3,
        feasibility: uc.feasibilityScore ?? 3,
        urgency: uc.urgency ?? 3,
        reusability: uc.reusability ?? 3,
        dataReadiness: uc.dataReadiness ?? 3,
      },
    ])
  )
}

const scoreFields: { key: keyof ScoreWeights; label: string; weight: number; description: string }[] = [
  { key: "businessImpact", label: "Business Impact", weight: 30, description: "Revenue, cost, productivity impact" },
  { key: "riskComplianceValue", label: "Risk / Compliance Value", weight: 20, description: "Regulatory and risk mitigation value" },
  { key: "feasibility", label: "Feasibility", weight: 20, description: "Technical and operational feasibility" },
  { key: "urgency", label: "Urgency", weight: 15, description: "Business urgency and deadline pressure" },
  { key: "reusability", label: "Reusability", weight: 10, description: "Potential to reuse across departments" },
  { key: "dataReadiness", label: "Data Readiness", weight: 5, description: "Availability and quality of required data" },
]

function ScoreCard({ ucId, scores, onUpdate }: { ucId: string; scores: ScoreWeights; onUpdate: (k: keyof ScoreWeights, v: number) => void }) {
  const uc = useCases.find((u) => u.id === ucId)!
  const score = calcScore(scores)
  const band = calcBand(score)

  const bandColors: Record<PriorityBand, string> = {
    P1: "text-red-700 bg-red-50 border-red-200",
    P2: "text-orange-700 bg-orange-50 border-orange-200",
    P3: "text-yellow-700 bg-yellow-50 border-yellow-200",
    P4: "text-gray-600 bg-gray-50 border-gray-200",
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-sm leading-snug">{uc.name}</CardTitle>
            <CardDescription className="text-xs mt-0.5">{uc.department} · {uc.id}</CardDescription>
          </div>
          <div className={`flex flex-col items-center rounded-lg border px-3 py-2 min-w-16 ${bandColors[band]}`}>
            <span className="text-2xl font-bold leading-none">{score}</span>
            <span className="text-xs font-medium mt-0.5">{band}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {scoreFields.map((field) => (
          <div key={field.key} className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-medium text-foreground">{field.label}</span>
                <span className="text-xs text-muted-foreground ml-2">({field.weight}% weight)</span>
              </div>
              <span className="text-sm font-bold text-foreground w-6 text-right">{scores[field.key]}</span>
            </div>
            <Slider
              min={1}
              max={5}
              step={1}
              value={[scores[field.key]]}
              onValueChange={(v) => onUpdate(field.key, Array.isArray(v) ? v[0] : v)}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1 — Low</span>
              <span>5 — High</span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

export function PriorityMatrixContent() {
  const [scores, setScores] = useState<UCScores>(initScores())
  const [calculating, setCalculating] = useState(false)

  function updateScore(ucId: string, field: keyof ScoreWeights, value: number) {
    setScores((prev) => ({
      ...prev,
      [ucId]: { ...prev[ucId], [field]: value },
    }))
  }

  async function handleRecalculate() {
    setCalculating(true)
    try {
      await Promise.all(
        useCases.map((uc) =>
          fetch("http://95.217.189.103:5000/api/priority-score", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ useCaseId: uc.id, ...scores[uc.id] }),
          })
        )
      )
      toast.success("Priority scores recalculated for all use cases.")
    } catch {
      toast.error("Recalculation failed.")
    } finally {
      setCalculating(false)
    }
  }

  const ranked = [...useCases]
    .map((uc) => ({ uc, score: calcScore(scores[uc.id]), band: calcBand(calcScore(scores[uc.id])) }))
    .sort((a, b) => b.score - a.score)

  return (
    <div className="p-6 flex flex-col gap-6">
      {/* Ranking table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="size-4" />
                Use Case Priority Ranking
              </CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Sorted by priority score. Adjust scores below to recalculate.
              </CardDescription>
            </div>
            <Button
              onClick={handleRecalculate}
              disabled={calculating}
              size="sm"
              className="gap-2"
            >
              <RefreshCw className={`size-4 ${calculating ? "animate-spin" : ""}`} />
              Recalculate Priority
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs font-medium text-muted-foreground px-5 py-2.5">Rank</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Use Case</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Department</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Impact</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Risk/Comp.</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Feasibility</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Urgency</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Reuse</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Data</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Score</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Band</th>
                </tr>
              </thead>
              <tbody>
                {ranked.map(({ uc, score, band }, i) => (
                  <tr key={uc.id} className={`border-b border-border last:border-0 ${i % 2 === 0 ? "" : "bg-muted/20"}`}>
                    <td className="px-5 py-3">
                      <span className={`size-6 rounded-full flex items-center justify-center text-xs font-bold
                        ${i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-gray-100 text-gray-600" : i === 2 ? "bg-orange-50 text-orange-600" : "text-muted-foreground"}`}>
                        {i + 1}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-medium">{uc.name}</span>
                        <span className="text-xs text-muted-foreground font-mono">{uc.id}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{uc.department}</td>
                    {(["businessImpact", "riskComplianceValue", "feasibility", "urgency", "reusability", "dataReadiness"] as (keyof ScoreWeights)[]).map((k) => (
                      <td key={k} className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <div className="flex gap-0.5">
                            {[1,2,3,4,5].map((n) => (
                              <span key={n} className={`size-1.5 rounded-full ${n <= scores[uc.id][k] ? "bg-primary" : "bg-muted"}`} />
                            ))}
                          </div>
                          <span className="text-xs font-medium ml-1">{scores[uc.id][k]}</span>
                        </div>
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <span className="text-lg font-bold text-foreground">{score}</span>
                    </td>
                    <td className="px-4 py-3">
                      <PriorityBadge value={band} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Score cards */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Edit Scores</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {useCases.map((uc) => (
            <ScoreCard
              key={uc.id}
              ucId={uc.id}
              scores={scores[uc.id]}
              onUpdate={(k, v) => updateScore(uc.id, k, v)}
            />
          ))}
        </div>
      </div>

      {/* Formula */}
      <Card className="bg-muted/30">
        <CardContent className="pt-4 pb-4">
          <p className="text-xs font-medium text-muted-foreground mb-2">Priority Score Formula</p>
          <p className="text-xs text-foreground font-mono">
            Score = (Business Impact × 0.30) + (Risk/Compliance × 0.20) + (Feasibility × 0.20) + (Urgency × 0.15) + (Reusability × 0.10) + (Data Readiness × 0.05) → scaled to 100
          </p>
          <div className="flex flex-wrap gap-3 mt-3">
            {[
              { band: "P1", label: "80–100: Start First", color: "text-red-700" },
              { band: "P2", label: "60–79: Strong Candidate", color: "text-orange-700" },
              { band: "P3", label: "40–59: Needs Discovery", color: "text-yellow-700" },
              { band: "P4", label: "<40: Park for Now", color: "text-gray-600" },
            ].map((b) => (
              <span key={b.band} className={`text-xs font-medium ${b.color}`}>{b.label}</span>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
